import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Tooltip,
  Typography,
  Card,
  Input,
  Checkbox,
  Tag,
  DatePicker,
  Select,
  Modal,
} from "antd";
import axios from "axios";
import { adalApiFetch } from ".././adalConfig";
import * as api from ".././constants/api";
import "antd/dist/reset.css";
import { CopyOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
const { Title } = Typography;
const { TextArea } = Input;

// Resizable column header component
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <th {...restProps} style={{ ...restProps.style, position: "relative" }}>
      {restProps.children}
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          width: "8px",
          cursor: "col-resize",
          userSelect: "none",
          touchAction: "none",
          zIndex: 1,
          borderRight: "1px solid #e8e8e8",
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const startWidth = width;

          const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const newWidth = Math.max(50, startWidth + deltaX);
            onResize(newWidth);
          };

          const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
          };

          document.addEventListener("mousemove", handleMouseMove);
          document.addEventListener("mouseup", handleMouseUp);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderRight = "2px solid #1890ff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderRight = "1px solid #e8e8e8";
        }}
      />
    </th>
  );
};

// Status options for Error Status column
const statusOptions = [
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

// Move columns definition inside component to access handleStatusChange

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    message.success("Copied to clipboard!");
  });
};

// Function to extract email addresses from text
const extractEmailFromText = (text) => {
  if (!text || typeof text !== "string") {
    return "None";
  }

  // Email regex pattern - standard email format
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const matches = text.match(emailPattern);

  if (matches && matches.length > 0) {
    // Return the first email found (or join multiple with commas if needed)
    return matches.join(", ");
  }

  return "None";
};

const Errors4003 = () => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [loading, setLoading] = useState(false);
  const [errorStatuses, setErrorStatuses] = useState({}); // Local state to track status changes
  const [registrationStatuses, setRegistrationStatuses] = useState({}); // Local state to track registration status changes
  const [errorModifiedDates, setErrorModifiedDates] = useState({}); // Local state to track modified dates
  const [errorResolutionNotes, setErrorResolutionNotes] = useState({}); // Local state to track resolution notes
  const [jiraValues, setJiraValues] = useState({}); // Local state to track Jira field values
  const [jiraEditingStates, setJiraEditingStates] = useState({}); // Local state to track which Jira fields are being edited
  const [savingStates, setSavingStates] = useState({}); // Local state to track saving status
  const [hasChanges, setHasChanges] = useState({}); // Local state to track if row has unsaved changes
  const [openDatePickers, setOpenDatePickers] = useState({});
  const [availableEmails, setAvailableEmails] = useState([]); // List of unique email addresses for dropdown
  const [availableJira, setAvailableJira] = useState([]); // List of unique Jira tickets for dropdown

  // Column widths state for resizable columns
  const [columnWidths, setColumnWidths] = useState({
    id: 50,
    jira: 120,
    exception: 200,
    methodName: 150,
    userEmail: 150,
    data: 150,
    createdDate: 150,
    registrationStatus: 150,
    errorStatus: 150,
    errorModifiedDate: 150,
    errorResolutionNotes: 150,
    actions: 100,
  });

  const handleResize = (columnKey) => (newWidth) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: newWidth,
    }));
  };

  // Column filter states
  const [filters, setFilters] = useState({
    jira: [], // Array for multiple Jira selection
    exception: "",
    methodName: "",
    userEmail: [], // Array for multiple email selection
    data: "",
    createdDate: null,
    registrationStatus: "",
    errorStatus: "",
    errorResolutionNotes: "",
    errorModifiedDate: null,
  });

  // Bulk update states
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkUpdateModalVisible, setBulkUpdateModalVisible] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({
    jiraTicket: "",
    errorStatus: "",
    registrationStatus: "",
    errorModifiedDate: null,
    errorResolutionNotes: "",
  });
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);

  // Handle status change for individual error records
  const handleStatusChange = (errorId, statusValue, checked) => {
    setErrorStatuses((prev) => ({
      ...prev,
      [errorId]: checked ? statusValue : "Pending", // Default to Pending if unchecked
    }));

    setErrorModifiedDates((prev) => ({
      ...prev,
      [errorId]: null,
    }));

    // Open the date picker for any status change
    setOpenDatePickers((prev) => ({
      ...prev,
      [errorId]: true,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));

    // TODO: Here you would make an API call to update the backend
    // For now, we're just updating the local state
    const statusText = checked ? statusValue : "Pending";
    console.log(`Updating error ${errorId} status to ${statusText}`);
  };

  // Handle date change for error modified date
  const handleDateChange = (errorId, date) => {
    setErrorModifiedDates((prev) => ({
      ...prev,
      [errorId]: date,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));
  };

  // Handle notes change for error resolution notes
  const handleNotesChange = (errorId, notes) => {
    setErrorResolutionNotes((prev) => ({
      ...prev,
      [errorId]: notes,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));
  };

  // Handle Jira field change
  const handleJiraChange = (errorId, jiraValue) => {
    // Extract ticket ID if user pastes full URL
    let cleanedValue = jiraValue;
    const jiraUrlPattern = /https?:\/\/jirasw\.nvidia\.com\/browse\//gi;
    if (jiraUrlPattern.test(jiraValue)) {
      cleanedValue = jiraValue.replace(jiraUrlPattern, "");
    }

    // Sanitize input: Remove any potentially malicious characters
    // Only allow alphanumeric characters, hyphens, and underscores (standard Jira ticket format)
    cleanedValue = cleanedValue.replace(/[^a-zA-Z0-9\-_]/g, "");

    // Validate Jira ticket format (PROJECT-123 or PROJECT_123)
    // Typical format: 1-10 uppercase letters, followed by hyphen/underscore, followed by 1-6 digits
    const jiraTicketPattern = /^[A-Z]{1,10}[-_]\d{1,6}$/;

    // If value is not empty and doesn't match pattern, show warning
    if (cleanedValue && !jiraTicketPattern.test(cleanedValue)) {
      // Allow partial input while typing, but validate on blur
      // For now, just sanitize and let user continue typing
    }

    setJiraValues((prev) => ({
      ...prev,
      [errorId]: cleanedValue,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));
  };

  // Validate Jira ticket format on blur
  const validateJiraTicket = (errorId, jiraValue) => {
    if (!jiraValue) return true; // Empty is allowed (optional field)

    const jiraTicketPattern = /^[A-Z]{1,10}[-_]\d{1,6}$/;
    if (!jiraTicketPattern.test(jiraValue)) {
      message.warning(`Invalid Jira ticket format for ID ${errorId}.`);
      return false;
    }
    return true;
  };

  // Handle save changes for individual error record
  const handleSaveChanges = async (errorId) => {
    // Validate Jira ticket before saving
    const jiraValue = jiraValues[errorId] || "";
    if (jiraValue && !validateJiraTicket(errorId, jiraValue)) {
      return; // Don't save if validation fails
    }

    setSavingStates((prev) => ({
      ...prev,
      [errorId]: true,
    }));

    try {
      // Prepare complete payload with all editable fields
      const saveData = {
        errorId: errorId,
        errorStatus: errorStatuses[errorId] || "Pending",
        registrationStatus: registrationStatuses[errorId] || "Pending",
        errorModifiedDate: errorModifiedDates[errorId]
          ? dayjs(errorModifiedDates[errorId]).format("YYYY-MM-DD")
          : null,
        errorResolutionNotes: errorResolutionNotes[errorId] || "",
        jiraTicket: jiraValue, // Jira ticket ID (optional)
      };

      console.log("Saving data for error:", errorId, saveData);

      await adalApiFetch(axios, api.POST_UPDATE_ERROR, {
        method: "POST",
        data: saveData,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Mark as saved (reset change tracking)
      setHasChanges((prev) => ({
        ...prev,
        [errorId]: false,
      }));

      message.success(`Error ${errorId} saved successfully!`);
    } catch (error) {
      console.error("Error saving:", error);

      // Check if it's a token/authentication error
      if (
        error.message &&
        (error.message.includes("Token renewal") ||
          error.message.includes("timeout") ||
          error.message.includes("401") ||
          error.message.includes("Unauthorized"))
      ) {
        message.error({
          content: `Session expired. Please refresh the page and try again.`,
          duration: 6,
        });
      } else {
        message.error(
          `Failed to save error ${errorId}. ${error.message || ""}`,
        );
      }
    } finally {
      setSavingStates((prev) => ({
        ...prev,
        [errorId]: false,
      }));
    }
  };

  // Handle checkbox selection for bulk update
  const handleRowSelection = (errorId, checked) => {
    if (checked) {
      setSelectedRowIds((prev) => [...prev, errorId]);
    } else {
      setSelectedRowIds((prev) => prev.filter((id) => id !== errorId));
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = data.map((record) => record.id);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds([]);
    }
  };

  // Open bulk update modal
  const openBulkUpdateModal = () => {
    if (selectedRowIds.length === 0) {
      message.warning("Please select at least one row to update");
      return;
    }
    setBulkUpdateModalVisible(true);
  };

  // Handle bulk update save
  const handleBulkUpdateSave = async () => {
    if (
      !bulkUpdateData.jiraTicket &&
      !bulkUpdateData.errorStatus &&
      !bulkUpdateData.registrationStatus &&
      !bulkUpdateData.errorModifiedDate &&
      !bulkUpdateData.errorResolutionNotes
    ) {
      message.warning("Please fill in at least one field to update");
      return;
    }

    setBulkUpdateLoading(true);
    let successCount = 0;
    let failCount = 0;

    // Update records one by one
    for (const errorId of selectedRowIds) {
      try {
        const saveData = {
          errorId: errorId,
          errorStatus: bulkUpdateData.errorStatus || "Pending",
          registrationStatus: bulkUpdateData.registrationStatus || "Pending",
          errorModifiedDate: bulkUpdateData.errorModifiedDate
            ? dayjs(bulkUpdateData.errorModifiedDate).format("YYYY-MM-DD")
            : null,
          errorResolutionNotes: bulkUpdateData.errorResolutionNotes || "",
          jiraTicket: bulkUpdateData.jiraTicket || jiraValues[errorId] || "",
        };

        await adalApiFetch(axios, api.POST_UPDATE_ERROR, {
          method: "POST",
          data: saveData,
          headers: {
            "Content-Type": "application/json",
          },
        });

        successCount++;
      } catch (error) {
        console.error(`Error updating record ${errorId}:`, error);
        failCount++;

        // Check if it's a token/authentication error
        if (
          error.message &&
          (error.message.includes("Token renewal") ||
            error.message.includes("timeout") ||
            error.message.includes("401") ||
            error.message.includes("Unauthorized"))
        ) {
          // Stop processing remaining records and show error
          setBulkUpdateLoading(false);
          message.error({
            content: `Session expired while updating records. ${successCount} records were saved. Please refresh the page and try again for the remaining records.`,
            duration: 8,
          });

          // Close modal and reset
          setBulkUpdateModalVisible(false);
          setBulkUpdateData({
            jiraTicket: "",
            errorStatus: "",
            registrationStatus: "",
            errorModifiedDate: null,
            errorResolutionNotes: "",
          });
          setSelectedRowIds([]);

          // Refresh data
          fetchDataWithFilters({
            results: pagination.pageSize,
            page: pagination.current,
            filters,
          });

          return; // Exit the function early
        }
      }
    }

    setBulkUpdateLoading(false);

    // Show results
    if (failCount === 0) {
      message.success(`Successfully updated ${successCount} record(s)!`);
    } else {
      message.warning(
        `Updated ${successCount} record(s), ${failCount} failed.`,
      );
    }

    // Close modal and reset
    setBulkUpdateModalVisible(false);
    setBulkUpdateData({
      jiraTicket: "",
      errorStatus: "",
      registrationStatus: "",
      errorModifiedDate: null,
      errorResolutionNotes: "",
    });
    setSelectedRowIds([]);

    // Refresh data
    fetchDataWithFilters({
      results: pagination.pageSize,
      page: pagination.current,
      filters,
    });
  };

  // Define columns inside component to access handleStatusChange
  const columns = [
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox
              checked={selectedRowIds.length === data.length && data.length > 0}
              indeterminate={
                selectedRowIds.length > 0 && selectedRowIds.length < data.length
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <span>ID</span>
          </div>
        </div>
      ),
      dataIndex: "id",
      width: columnWidths.id,
      onHeaderCell: () => ({
        width: columnWidths.id,
        onResize: handleResize("id"),
      }),
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Checkbox
            checked={selectedRowIds.includes(record.id)}
            onChange={(e) => handleRowSelection(record.id, e.target.checked)}
          />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Jira</div>
          <Select
            mode="multiple"
            placeholder="Filter by Jira"
            value={filters.jira}
            onChange={(value) => handleFilterChange("jira", value || [])}
            size="small"
            style={{ width: "100%" }}
            allowClear
            showSearch
            maxTagCount="responsive"
            optionFilterProp="label"
            options={(() => {
              // Sort Jira tickets: selected ones first, then unselected (both alphabetically)
              const selected = availableJira
                .filter((jira) => filters.jira.includes(jira))
                .sort();
              const unselected = availableJira
                .filter((jira) => !filters.jira.includes(jira))
                .sort();
              return [...selected, ...unselected].map((jira) => ({
                value: jira,
                label: jira,
              }));
            })()}
            optionRender={(option) => {
              const isSelected = filters.jira.includes(option.value);
              return (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Checkbox checked={isSelected} />
                  <span>{option.label}</span>
                </div>
              );
            }}
          />
        </div>
      ),
      dataIndex: "jiraValue",
      width: columnWidths.jira,
      onHeaderCell: () => ({
        width: columnWidths.jira,
        onResize: handleResize("jira"),
      }),
      render: (text, record) => {
        const currentJiraValue = jiraValues[record.id] || "";
        const trimmedValue = currentJiraValue.replace(/\s+/g, ""); // Remove all spaces
        const jiraUrl = trimmedValue
          ? `https://jirasw.nvidia.com/browse/${trimmedValue}`
          : "";
        const isEditing = jiraEditingStates[record.id] || false;

        // Show input field when editing, otherwise show link
        if (isEditing || !trimmedValue) {
          return (
            <Input
              size="small"
              value={currentJiraValue}
              onChange={(e) => handleJiraChange(record.id, e.target.value)}
              onKeyDown={(e) => {
                // Allow: backspace, delete, tab, escape, enter, arrows, home, end
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "Escape",
                  "Enter",
                  "ArrowLeft",
                  "ArrowRight",
                  "ArrowUp",
                  "ArrowDown",
                  "Home",
                  "End",
                ];

                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Cmd+A, Cmd+C, Cmd+V, Cmd+X
                if (e.ctrlKey || e.metaKey) {
                  if (
                    ["a", "c", "v", "x", "A", "C", "V", "X"].includes(e.key)
                  ) {
                    return; // Allow copy/paste/select all
                  }
                }

                // If it's a special key, allow it
                if (allowedKeys.includes(e.key)) {
                  return;
                }

                // Only allow alphanumeric, hyphen, and underscore
                const validCharPattern = /^[a-zA-Z0-9\-_]$/;
                if (!validCharPattern.test(e.key)) {
                  e.preventDefault(); // Block the key
                  return;
                }
              }}
              onPaste={(e) => {
                // Handle paste event to clean pasted content
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");

                // Check if it's a URL
                const urlPattern = /^https?:\/\//i;
                if (urlPattern.test(pastedText)) {
                  // It's a URL - check if it's a valid Jira URL
                  const jiraUrlPattern =
                    /^https?:\/\/jirasw\.nvidia\.com\/browse\/.+/gi;
                  if (jiraUrlPattern.test(pastedText)) {
                    // Valid Jira URL - extract ticket ID
                    const cleanedValue = pastedText.replace(
                      /https?:\/\/jirasw\.nvidia\.com\/browse\//gi,
                      "",
                    );
                    handleJiraChange(record.id, cleanedValue);
                  } else {
                    // Invalid URL - reject it
                    message.error(
                      "Only Jira URLs from jirasw.nvidia.com are allowed!",
                    );
                  }
                  return;
                }

                // Not a URL - sanitize and allow
                let cleanedValue = pastedText;
                // Remove any invalid characters
                cleanedValue = cleanedValue.replace(/[^a-zA-Z0-9\-_]/g, "");

                // Update the value
                handleJiraChange(record.id, cleanedValue);
              }}
              onBlur={(e) => {
                // Remove spaces when user clicks outside
                const cleanedValue = e.target.value.replace(/\s+/g, "");
                if (cleanedValue !== e.target.value) {
                  handleJiraChange(record.id, cleanedValue);
                }

                // Validate the Jira ticket format
                const finalValue = jiraValues[record.id] || cleanedValue;
                validateJiraTicket(record.id, finalValue);

                setJiraEditingStates((prev) => ({
                  ...prev,
                  [record.id]: false,
                }));
              }}
              onFocus={() =>
                setJiraEditingStates((prev) => ({
                  ...prev,
                  [record.id]: true,
                }))
              }
              placeholder="Enter Jira ticket"
              style={{ fontSize: "10px" }}
            />
          );
        }

        // Show hyperlink when not editing and value exists
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "4px",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "2px",
              minHeight: "24px",
            }}
          >
            <a
              href={jiraUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "10px",
                color: "#1890ff",
                textDecoration: "underline",
                wordBreak: "break-all",
                flex: 1,
              }}
            >
              {trimmedValue}
            </a>
            <EditOutlined
              style={{
                fontSize: "12px",
                color: "#1890ff",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={() =>
                setJiraEditingStates((prev) => ({
                  ...prev,
                  [record.id]: true,
                }))
              }
            />
          </div>
        );
      },
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>ErrorMsg</div>
          <Input
            placeholder="Search Error"
            value={filters.exception}
            onChange={(e) => handleFilterChange("exception", e.target.value)}
            size="small"
            style={{ width: "100%" }}
            allowClear
          />
        </div>
      ),
      dataIndex: "exception",
      width: columnWidths.exception,
      onHeaderCell: () => ({
        width: columnWidths.exception,
        onResize: handleResize("exception"),
      }),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>ErrorOrigin</div>
          <Input
            placeholder="Search Origin"
            value={filters.methodName}
            onChange={(e) => handleFilterChange("methodName", e.target.value)}
            size="small"
            style={{ width: "100%" }}
            allowClear
          />
        </div>
      ),
      dataIndex: "methodName",
      width: columnWidths.methodName,
      onHeaderCell: () => ({
        width: columnWidths.methodName,
        onResize: handleResize("methodName"),
      }),
      render: (text) => text.replace("Post", ""),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>User Email Address</div>
          <Select
            mode="multiple"
            placeholder="Filter by Email"
            value={filters.userEmail}
            onChange={(value) => handleFilterChange("userEmail", value || [])}
            size="small"
            style={{ width: "100%" }}
            allowClear
            showSearch
            maxTagCount="responsive"
            optionFilterProp="label"
            options={(() => {
              // Sort emails: selected ones first, then unselected (both alphabetically)
              const selected = availableEmails
                .filter((email) => filters.userEmail.includes(email))
                .sort();
              const unselected = availableEmails
                .filter((email) => !filters.userEmail.includes(email))
                .sort();
              return [...selected, ...unselected].map((email) => ({
                value: email,
                label: email,
              }));
            })()}
            optionRender={(option) => {
              const isSelected = filters.userEmail.includes(option.value);
              return (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Checkbox checked={isSelected} />
                  <span>{option.label}</span>
                </div>
              );
            }}
          />
        </div>
      ),
      dataIndex: "userEmail",
      width: columnWidths.userEmail,
      onHeaderCell: () => ({
        width: columnWidths.userEmail,
        onResize: handleResize("userEmail"),
      }),
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tooltip title={text}>
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {text}
            </span>
          </Tooltip>
          {text !== "None" && (
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(text)}
              style={{ marginLeft: 8, flexShrink: 0 }}
            />
          )}
        </div>
      ),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Data</div>
          <Input
            placeholder="Search Data"
            value={filters.data}
            onChange={(e) => handleFilterChange("data", e.target.value)}
            size="small"
            style={{ width: "100%" }}
            allowClear
          />
        </div>
      ),
      dataIndex: "data",
      width: columnWidths.data,
      onHeaderCell: () => ({
        width: columnWidths.data,
        onResize: handleResize("data"),
      }),
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tooltip title={text}>
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {text}
            </span>
          </Tooltip>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(text)}
            style={{ marginLeft: 8, flexShrink: 0 }}
          />
        </div>
      ),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Created Date</div>
          <DatePicker
            placeholder="Filter by Date"
            value={filters.createdDate ? dayjs(filters.createdDate) : null}
            onChange={(date) => handleFilterChange("createdDate", date)}
            size="small"
            style={{ width: "100%" }}
            allowClear
            format="MM/DD/YYYY"
          />
        </div>
      ),
      dataIndex: "createdDate",
      width: columnWidths.createdDate,
      onHeaderCell: () => ({
        width: columnWidths.createdDate,
        onResize: handleResize("createdDate"),
      }),
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Registration Status</div>
          <Select
            placeholder="Filter Status"
            value={filters.registrationStatus || undefined}
            onChange={(value) =>
              handleFilterChange("registrationStatus", value || "")
            }
            size="small"
            style={{ width: "100%" }}
            allowClear
            options={[
              { value: "Completed", label: "Completed" },
              { value: "Errored Out", label: "Errored Out" },
              { value: "Pending", label: "Pending" },
            ]}
          />
        </div>
      ),
      dataIndex: "registrationStatus",
      width: columnWidths.registrationStatus,
      onHeaderCell: () => ({
        width: columnWidths.registrationStatus,
        onResize: handleResize("registrationStatus"),
      }),
      render: (text, record) => {
        const registrationStatusOptions = [
          { value: "Completed", label: "Completed" },
          { value: "Errored Out", label: "Errored Out" },
          { value: "Pending", label: "Pending" },
        ];

        const currentStatus =
          registrationStatuses[record.id] || text || "Pending";

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {registrationStatusOptions.map((option) => {
              return (
                <div
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Checkbox
                    checked={currentStatus === option.value}
                    onChange={() => {
                      setRegistrationStatuses({
                        ...registrationStatuses,
                        [record.id]: option.value,
                      });
                      setHasChanges({ ...hasChanges, [record.id]: true });
                    }}
                    size="small"
                    className="green-checkbox"
                  />
                  <span
                    style={{
                      fontSize: "12px",
                      color: currentStatus === option.value ? "black" : "#999",
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Error Status</div>
          <Select
            placeholder="Filter Status"
            value={filters.errorStatus || undefined}
            onChange={(value) => handleFilterChange("errorStatus", value || "")}
            size="small"
            style={{ width: "100%" }}
            allowClear
            options={[
              { value: "Pending", label: "Pending" },
              { value: "In Progress", label: "In Progress" },
              { value: "Completed", label: "Completed" },
            ]}
          />
        </div>
      ),
      dataIndex: "errorStatus",
      width: columnWidths.errorStatus,
      onHeaderCell: () => ({
        width: columnWidths.errorStatus,
        onResize: handleResize("errorStatus"),
      }),
      render: (text, record) => {
        const currentStatus = errorStatuses[record.id] || text || "Pending";
        const savedStatus = text || "Pending";
        const isCompletedAndSaved = savedStatus === "Completed";

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {statusOptions.map((option) => {
              // If status is saved as "Completed" in database, only show the "Completed" option
              if (isCompletedAndSaved && option.value !== "Completed") {
                return null;
              }

              const isThisOptionCompletedAndSaved =
                isCompletedAndSaved && option.value === "Completed";

              return (
                <div
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    opacity: isThisOptionCompletedAndSaved ? 0.6 : 1, // Grey out only when completed AND saved
                  }}
                >
                  <Checkbox
                    checked={currentStatus === option.value}
                    onChange={(e) =>
                      handleStatusChange(
                        record.id,
                        option.value,
                        e.target.checked,
                      )
                    }
                    size="small"
                    className="green-checkbox"
                    disabled={isThisOptionCompletedAndSaved} // Disable only if completed AND saved
                  />
                  <Tag
                    style={{
                      margin: 0,
                      fontSize: "9px",
                      padding: "2px 6px",
                      lineHeight: "1.2",
                      border: isThisOptionCompletedAndSaved
                        ? "1px solid #d9d9d9"
                        : "1px solid black",
                      backgroundColor: isThisOptionCompletedAndSaved
                        ? "#f5f5f5"
                        : "white",
                      color: isThisOptionCompletedAndSaved
                        ? "#8c8c8c"
                        : "black",
                      width: "80px",
                      textAlign: "center",
                      display: "inline-block",
                      height: "20px",
                      boxSizing: "border-box",
                      cursor: isThisOptionCompletedAndSaved
                        ? "not-allowed"
                        : "default",
                    }}
                  >
                    {option.label}
                  </Tag>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Error Modified Date</div>
          <DatePicker
            placeholder="Filter by Date"
            value={
              filters.errorModifiedDate
                ? dayjs(filters.errorModifiedDate)
                : null
            }
            onChange={(date) => handleFilterChange("errorModifiedDate", date)}
            size="small"
            style={{ width: "100%" }}
            allowClear
            format="MM/DD/YYYY"
          />
        </div>
      ),
      dataIndex: "errorModifiedDate",
      width: columnWidths.errorModifiedDate,
      onHeaderCell: () => ({
        width: columnWidths.errorModifiedDate,
        onResize: handleResize("errorModifiedDate"),
      }),
      render: (text, record) => {
        const currentDate = errorModifiedDates[record.id] || null;
        const isOpen = openDatePickers[record.id] || false;
        return (
          <DatePicker
            size="small"
            value={currentDate ? dayjs(currentDate) : null}
            open={isOpen}
            onChange={(date) => {
              handleDateChange(record.id, date);
              // Close the date picker after selection
              setOpenDatePickers((prev) => ({
                ...prev,
                [record.id]: false,
              }));
            }}
            onOpenChange={(open) => {
              // Update the open state when user manually opens/closes
              setOpenDatePickers((prev) => ({
                ...prev,
                [record.id]: open,
              }));
            }}
            placeholder="Select date"
            style={{ width: "100%" }}
            format="MM/DD/YYYY"
          />
        );
      },
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: "auto" }}>Error Resolution Notes</div>
          <Input
            placeholder="Search Notes"
            value={filters.errorResolutionNotes}
            onChange={(e) =>
              handleFilterChange("errorResolutionNotes", e.target.value)
            }
            size="small"
            style={{ width: "100%" }}
            allowClear
          />
        </div>
      ),
      dataIndex: "errorResolutionNotes",
      width: columnWidths.errorResolutionNotes,
      onHeaderCell: () => ({
        width: columnWidths.errorResolutionNotes,
        onResize: handleResize("errorResolutionNotes"),
      }),
      render: (text, record) => {
        const currentNotes = errorResolutionNotes[record.id] || "";
        return (
          <Input.TextArea
            size="small"
            value={currentNotes}
            onChange={(e) => handleNotesChange(record.id, e.target.value)}
            placeholder="Enter resolution notes..."
            rows={2}
            style={{ fontSize: "10px" }}
          />
        );
      },
    },
    {
      title: () => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            minHeight: "80px",
            width: "100%",
          }}
        >
          <div>Actions</div>
        </div>
      ),
      dataIndex: "actions",
      width: columnWidths.actions,
      onHeaderCell: () => ({
        width: columnWidths.actions,
        onResize: handleResize("actions"),
      }),
      render: (text, record) => {
        const isSaving = savingStates[record.id] || false;
        const hasUnsavedChanges = hasChanges[record.id] || false;

        return (
          <Button
            type="primary"
            size="small"
            loading={isSaving}
            disabled={!hasUnsavedChanges}
            onClick={() => handleSaveChanges(record.id)}
            style={{
              backgroundColor: hasUnsavedChanges ? "#52c41a" : "#d9d9d9",
              borderColor: hasUnsavedChanges ? "#52c41a" : "#d9d9d9",
              fontSize: "10px",
              height: "24px",
              padding: "0 8px",
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        );
      },
    },
  ];

  // Handle column filter change
  const handleFilterChange = (columnName, value) => {
    setFilters((prev) => ({
      ...prev,
      [columnName]: value,
    }));

    // Reset to page 1 when filtering
    setPagination((prev) => ({ ...prev, current: 1 }));

    // Fetch data with new filters
    const newFilters = { ...filters, [columnName]: value };
    fetchDataWithFilters({
      results: pagination.pageSize,
      page: 1,
      filters: newFilters,
    });
  };

  // Helper function to escape quotes for Dynamic LINQ
  const escapeForDynamicLinq = (value) => {
    if (!value) return value;
    // Replace double quotes with two double quotes (LINQ Dynamic Core syntax)
    return value.replace(/"/g, '""');
  };

  const buildFilter = (filterValues) => {
    const filterConditions = [];

    if (filterValues.jira && filterValues.jira.length > 0) {
      // Handle multiple Jira selections with OR logic
      const jiraConditions = filterValues.jira.map(
        (jira) => `JiraTicket.Contains("${escapeForDynamicLinq(jira)}")`,
      );
      filterConditions.push(`(${jiraConditions.join(" OR ")})`);
    }
    if (filterValues.exception) {
      filterConditions.push(
        `Exception.Contains("${escapeForDynamicLinq(filterValues.exception)}")`,
      );
    }
    if (filterValues.methodName) {
      filterConditions.push(
        `MethodName.Contains("${escapeForDynamicLinq(
          filterValues.methodName,
        )}")`,
      );
    }
    if (filterValues.userEmail && filterValues.userEmail.length > 0) {
      // Handle multiple email selections with OR logic
      const emailConditions = filterValues.userEmail.map(
        (email) => `MethodName.Contains("${escapeForDynamicLinq(email)}")`,
      );
      filterConditions.push(`(${emailConditions.join(" OR ")})`);
    }
    if (filterValues.data) {
      filterConditions.push(
        `Data.Contains("${escapeForDynamicLinq(filterValues.data)}")`,
      );
    }
    if (filterValues.createdDate) {
      const selectedDate = dayjs(filterValues.createdDate).format("YYYY-MM-DD");
      const nextDate = dayjs(filterValues.createdDate)
        .add(1, "day")
        .format("YYYY-MM-DD");
      filterConditions.push(
        `CreatedDate >= "${selectedDate}" AND CreatedDate < "${nextDate}"`,
      );
    }
    if (filterValues.registrationStatus) {
      filterConditions.push(
        `RegistrationStatus == "${escapeForDynamicLinq(
          filterValues.registrationStatus,
        )}"`,
      );
    }
    if (filterValues.errorStatus) {
      filterConditions.push(
        `ErrorStatus == "${escapeForDynamicLinq(filterValues.errorStatus)}"`,
      );
    }
    if (filterValues.errorResolutionNotes) {
      filterConditions.push(
        `ErrorResolutionNotes.Contains("${escapeForDynamicLinq(
          filterValues.errorResolutionNotes,
        )}")`,
      );
    }
    if (filterValues.errorModifiedDate) {
      // Date filtering requires backend code changes that cannot be deployed
      // due to pre-existing compilation errors in the codebase
      message.warning({
        content:
          "Date filtering is not currently supported. Please use other filters.",
        duration: 3,
        key: "date-filter-warning",
      });
    }

    return filterConditions.length > 0 ? filterConditions.join(" AND ") : "";
  };
  // ✅ Fetch data with filters
  const fetchDataWithFilters = ({ results, page, filters: filterValues }) => {
    setLoading(true);

    const currentFilters = filterValues || filters;
    let filterString = buildFilter(currentFilters);

    const take = results || 10;
    const skip = results * (page - 1) || 0;
    const orderby = "id desc";

    // Use POST method to avoid URL length limits for long filter strings
    let apiCall = adalApiFetch(axios, api.POST_REGISTRATIONERRORS, {
      method: "POST",
      data: {
        filter: filterString,
        take: take,
        skip: skip,
        orderBy: orderby,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    apiCall
      .then((response) => {
        const modifiedData = response.data.records.map((r) => ({
          ...r,
          createdDate: r.createdDate ? r.createdDate.split("T")[0] : "",
          entitlement: r.pakid && r.pakid !== "" ? r.pakid : r.serialNumber,
          errorStatus: r.errorStatus || "Pending", // Initialize with 'Pending' if no status from API
          userEmail: extractEmailFromText(r.methodName), // Extract email from ErrorOrigin (methodName)
        }));

        setData(modifiedData);

        // Initialize state variables from API response
        const initialStatuses = {};
        const initialDates = {};
        const initialNotes = {};
        const initialJira = {};

        response.data.records.forEach((record) => {
          if (record.errorStatus) {
            initialStatuses[record.id] = record.errorStatus;
          }
          if (record.errorModifiedDate) {
            initialDates[record.id] = dayjs(record.errorModifiedDate);
          }
          if (record.errorResolutionNotes) {
            initialNotes[record.id] = record.errorResolutionNotes;
          }
          if (record.jiraTicket) {
            initialJira[record.id] = record.jiraTicket;
          }
        });

        setErrorStatuses(initialStatuses);
        setErrorModifiedDates(initialDates);
        setErrorResolutionNotes(initialNotes);
        setJiraValues(initialJira);

        setPagination((prev) => ({
          ...prev,
          total: response.data.totalCount,
          current: page,
          pageSize: results || prev.pageSize,
        }));
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  // Fetch all emails for dropdown (fetch all records to extract unique emails)
  const fetchAllEmails = () => {
    // Fetch all records without pagination to get complete email list
    adalApiFetch(axios, api.POST_REGISTRATIONERRORS, {
      method: "POST",
      data: {
        filter: "",
        take: 10000, // Large number to get all records
        skip: 0,
        orderBy: "id desc",
      },
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        // Extract unique email addresses from all records
        const emailSet = new Set();
        response.data.records.forEach((record) => {
          const email = extractEmailFromText(record.methodName);
          if (email && email !== "None") {
            // If multiple emails are comma-separated, split them
            const emails = email.split(", ").map((e) => e.trim());
            emails.forEach((e) => emailSet.add(e));
          }
        });
        const uniqueEmails = Array.from(emailSet).sort();
        setAvailableEmails(uniqueEmails);

        // Extract unique Jira tickets from all records
        const jiraSet = new Set();
        response.data.records.forEach((record) => {
          if (record.jiraTicket && record.jiraTicket.trim() !== "") {
            jiraSet.add(record.jiraTicket.trim());
          }
        });
        const uniqueJira = Array.from(jiraSet).sort();
        setAvailableJira(uniqueJira);
      })
      .catch((error) => console.error("Error fetching emails:", error));
  };

  useEffect(() => {
    fetchDataWithFilters({
      results: pagination.pageSize,
      page: pagination.current,
      filters,
    });
    fetchAllEmails(); // Fetch all emails and Jira tickets for the dropdowns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = Runs only on mount

  const handleTableChange = (paginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
    fetchDataWithFilters({
      results: paginationConfig.pageSize,
      page: paginationConfig.current,
      filters,
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      filters.jira.length > 0 ||
      filters.exception !== "" ||
      filters.methodName !== "" ||
      filters.userEmail.length > 0 ||
      filters.data !== "" ||
      filters.createdAt !== null ||
      filters.registrationStatus !== "" ||
      filters.errorStatus !== "" ||
      filters.errorResolutionNotes !== "" ||
      filters.errorModifiedDate !== null
    );
  };

  // Reset all filters
  const handleResetFilters = () => {
    const resetFilters = {
      jira: [],
      exception: "",
      methodName: "",
      userEmail: [],
      data: "",
      createdAt: null,
      registrationStatus: "",
      errorStatus: "",
      errorResolutionNotes: "",
      errorModifiedDate: null,
    };
    setFilters(resetFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchDataWithFilters({
      results: pagination.pageSize,
      page: 1,
      filters: resetFilters,
    });
  };

  return (
    <Card>
      <style>
        {`
          .green-checkbox .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #52c41a !important;
            border-color: #52c41a !important;
          }
          .green-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {
            border-color: white !important;
          }
          .green-checkbox:hover .ant-checkbox-inner {
            border-color: #52c41a !important;
          }
        `}
      </style>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Title level={4} style={{ margin: 0 }}>
            Registration Error Details
          </Title>
          <Tag color="blue" style={{ fontSize: "14px", padding: "4px 12px" }}>
            {pagination.total || 0} record{pagination.total !== 1 ? "s" : ""}
          </Tag>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {hasActiveFilters() && (
            <Button
              onClick={handleResetFilters}
              style={{
                backgroundColor: "#76b900",
                borderColor: "#76b900",
                color: "white",
              }}
            >
              Reset Filters
            </Button>
          )}
          <Button
            type="primary"
            onClick={openBulkUpdateModal}
            disabled={selectedRowIds.length === 0}
            style={{
              backgroundColor:
                selectedRowIds.length === 0 ? "#b8e986" : "#76b900",
              borderColor: selectedRowIds.length === 0 ? "#b8e986" : "#76b900",
              cursor: selectedRowIds.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            Bulk Update{" "}
            {selectedRowIds.length > 0 && `(${selectedRowIds.length} selected)`}
          </Button>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "left",
          marginBottom: 20,
        }}
      >
        <Table
          size="small"
          rowClassName={() => "rowClassName1"}
          bordered
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
          }}
          loading={loading}
          onChange={handleTableChange}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
        />
      </div>

      {/* Bulk Update Modal */}
      <Modal
        title="Bulk Update Error Records"
        open={bulkUpdateModalVisible}
        onCancel={() => {
          setBulkUpdateModalVisible(false);
          setBulkUpdateData({
            jiraTicket: "",
            errorStatus: "",
            registrationStatus: "",
            errorModifiedDate: null,
            errorResolutionNotes: "",
          });
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setBulkUpdateModalVisible(false);
              setBulkUpdateData({
                jiraTicket: "",
                errorStatus: "",
                registrationStatus: "",
                errorModifiedDate: null,
                errorResolutionNotes: "",
              });
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={bulkUpdateLoading}
            onClick={handleBulkUpdateSave}
            style={{ backgroundColor: "#76b900", borderColor: "#76b900" }}
          >
            Save
          </Button>,
        ]}
        width={600}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <div style={{ marginBottom: 8, fontWeight: "500" }}>
              Jira Ticket
            </div>
            <Input
              placeholder="Enter Jira ticket (e.g., ITSWS-2183)"
              value={bulkUpdateData.jiraTicket}
              onChange={(e) => {
                // Clean the input - remove any URLs and sanitize
                let cleanedValue = e.target.value;
                const jiraUrlPattern =
                  /https?:\/\/jirasw\.nvidia\.com\/browse\//gi;
                if (jiraUrlPattern.test(cleanedValue)) {
                  cleanedValue = cleanedValue.replace(jiraUrlPattern, "");
                }
                // Only allow alphanumeric, hyphen, and underscore
                cleanedValue = cleanedValue.replace(/[^a-zA-Z0-9\-_]/g, "");
                setBulkUpdateData((prev) => ({
                  ...prev,
                  jiraTicket: cleanedValue,
                }));
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");
                const urlPattern = /^https?:\/\//i;
                if (urlPattern.test(pastedText)) {
                  const jiraUrlPattern =
                    /^https?:\/\/jirasw\.nvidia\.com\/browse\/.+/gi;
                  if (jiraUrlPattern.test(pastedText)) {
                    const cleanedValue = pastedText.replace(
                      /https?:\/\/jirasw\.nvidia\.com\/browse\//gi,
                      "",
                    );
                    setBulkUpdateData((prev) => ({
                      ...prev,
                      jiraTicket: cleanedValue,
                    }));
                  } else {
                    message.error(
                      "Only Jira URLs from jirasw.nvidia.com are allowed!",
                    );
                  }
                } else {
                  const cleanedValue = pastedText.replace(
                    /[^a-zA-Z0-9\-_]/g,
                    "",
                  );
                  setBulkUpdateData((prev) => ({
                    ...prev,
                    jiraTicket: cleanedValue,
                  }));
                }
              }}
              allowClear
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: "500" }}>
              Error Status
            </div>
            <Select
              placeholder="Select Status"
              value={bulkUpdateData.errorStatus || undefined}
              onChange={(value) =>
                setBulkUpdateData((prev) => ({ ...prev, errorStatus: value }))
              }
              style={{ width: "100%" }}
              allowClear
              options={[
                { value: "Pending", label: "Pending" },
                { value: "In Progress", label: "In Progress" },
                { value: "Completed", label: "Completed" },
              ]}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: "500" }}>
              Registration Status
            </div>
            <Select
              placeholder="Select Registration Status"
              value={bulkUpdateData.registrationStatus || undefined}
              onChange={(value) =>
                setBulkUpdateData((prev) => ({
                  ...prev,
                  registrationStatus: value,
                }))
              }
              style={{ width: "100%" }}
              allowClear
              options={[
                { value: "Completed", label: "Completed" },
                { value: "Errored Out", label: "Errored Out" },
                { value: "Pending", label: "Pending" },
              ]}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: "500" }}>
              Error Modified Date
            </div>
            <DatePicker
              placeholder="Select Date"
              value={
                bulkUpdateData.errorModifiedDate
                  ? dayjs(bulkUpdateData.errorModifiedDate)
                  : null
              }
              onChange={(date) =>
                setBulkUpdateData((prev) => ({
                  ...prev,
                  errorModifiedDate: date,
                }))
              }
              style={{ width: "100%" }}
              format="MM/DD/YYYY"
              allowClear
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: "500" }}>
              Error Resolution Notes
            </div>
            <TextArea
              placeholder="Enter resolution notes..."
              value={bulkUpdateData.errorResolutionNotes}
              onChange={(e) =>
                setBulkUpdateData((prev) => ({
                  ...prev,
                  errorResolutionNotes: e.target.value,
                }))
              }
              rows={4}
            />
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default Errors4003;
